import { ClassroomIcon } from "@components/ClassroomIcon"
import { Badge } from "@components/ui/Badge"
import { Card, CardBody } from "@components/ui/Card"
import { useToast } from "@components/ui/Toaster"
import { useAcademicConfig } from "@hooks/useAcademicConfig"
import { useCutFormat } from "@hooks/useCutFormat"
import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatDate, formatGrade, formatPercent } from "@lib/intl"
import { logger } from "@lib/logger"
import {
  ArchiveRestore,
  ArrowRight,
  CalendarClock,
  ChartNoAxesColumnIncreasing,
  FileText,
  Loader2,
  Pencil,
  Users,
} from "lucide-react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { useRestoreClassroom } from "../hooks"

import { EditClassroomModal } from "./EditClassroomModal"

import { classroomAdaptiveHref } from "@/constants/adaptiveLevels"
import { gradeTier, gradeTierBgClass, gradeTierTextClass } from "@/constants/gradeTier"
import { apiErrorCode } from "@/lib/apiError"
import type { Classroom } from "@/types"

/**
 * Classroom figures read from the SHARED tier scale (PRD-59).
 *
 * This file used to bucket percentages itself at 80/65, under a docstring
 * claiming it "mirrors the thresholds the existing `<ProgressBar>` primitive
 * uses — single source of truth". No such primitive exists (the only
 * `ProgressBar` in the repo is `TaskProgressBar`, a Celery progress bar with
 * no thresholds), and 80/65 appeared in no other file. The comment asserted
 * the consistency it broke: `constants/gradeTier.ts` puts the GROUP
 * breakpoints at 85/75, so a classroom averaging 8.2/10 rendered emerald here
 * and amber in the sidebar pill 200px to its left, on the same screen.
 *
 * "group" mode on purpose: this is a whole classroom's average, not one
 * student's, and a group is held to the stricter bar.
 */
const figureTone = (pct: number) => gradeTierTextClass(gradeTier(pct, "0-100", "group"))
const figureBar = (pct: number) => gradeTierBgClass(gradeTier(pct, "0-100", "group"))

/**
 * Inline metric row: label · big value · colored progress bar. Used
 * for Promedio + Entregas so both academic-health signals share the
 * same visual vocabulary on the card.
 */
function MetricRow({
  label,
  display,
  pct,
  ariaLabel,
  empty,
}: {
  label: string
  display: string
  /** 0-100 — drives both the bar fill width and the tone bucket. */
  pct: number
  ariaLabel: string
  /** No records yet. A fresh classroom is not a failing classroom (PRD-59):
   *  `toneFor(0)` bucketed to "low", so the product's first act after a
   *  professor created their first salón was to paint two red bars at an
   *  object that cannot have data yet. `InsightsBar` already handled this
   *  correctly one click away. */
  empty?: boolean
}) {
  const clamped = Math.min(Math.max(pct, 0), 100)
  if (empty) {
    return (
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-label">{label}</span>
        <span className="text-ink-subtle text-base font-semibold tabular-nums">{display}</span>
      </div>
    )
  }
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-label">{label}</span>
        <span className={cn("text-base font-semibold tabular-nums", figureTone(pct))}>
          {display}
        </span>
      </div>
      <div
        className="bg-inset mt-1 h-1.5 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
      >
        <div
          className={cn("h-full rounded-full transition-[width] duration-200", figureBar(pct))}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  )
}

export function ClassroomCard({ classroom }: { classroom: Classroom }) {
  const { t } = useTranslation("classrooms")
  const { period_type: periodType } = useAcademicConfig()
  const gradeScale = useGradeScale()
  const cutFormat = useCutFormat()
  const navigate = useNavigate()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const restore = useRestoreClassroom()

  // PRD-45 §FR-4.3 — restore, right from the archived-tab card, no
  // confirm step (unlike archive: this is the reversible action's
  // reverse, nothing is at stake). It still stops propagation: the stretched
  // link sits underneath the whole card.
  const handleRestore = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      restore.mutate(classroom.id, {
        onSuccess: () => toast.success(t("card.restore_success")),
        onError: (err) => {
          const code = apiErrorCode(err)
          toast.error(t(`errors:${code}`, t("card.restore_error")))
          logger.error("classroom.restore.failed", { classroomId: classroom.id, code })
        },
      })
    },
    [restore, classroom.id, toast, t],
  )

  // True when professor has set a custom alias that differs from the computed label.
  const hasCustomAlias =
    !!classroom.classroom_alias && classroom.classroom_alias !== classroom.canonical_label

  // Edit pencil → open the name+icon modal. preventDefault + stopPropagation so
  // the click never reaches the stretched link beneath the card.
  const openEdit = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setEditing(true)
  }, [])

  // Materials CTA: deep-link into the filtered /materiales list. A button +
  // useNavigate rather than a second <Link>, so the card exposes exactly one
  // link (the stretched overlay) and one obvious "go somewhere else" control.
  const handleViewMaterials = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      navigate(`/materiales?classroom=${classroom.id}`)
    },
    [navigate, classroom.id],
  )

  // PRD-48 — level changes waiting on the professor. Home links here when
  // they span several classrooms, so each card says which ones hold them.
  const adaptivePending = classroom.is_archived ? 0 : (classroom.adaptive_pending_count ?? 0)
  const handleReviewAdaptive = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      navigate(classroomAdaptiveHref(classroom.id))
    },
    [navigate, classroom.id],
  )

  // Promedio is rendered on the institution's grade scale (PRD-38: resolved
  // tenant config, no longer a per-professor preference). The colored bar uses
  // the normalized percentage so a 7.2/10 and a 72/100 paint the same tone.
  const gradeMax = gradeScale === "0-100" ? 100 : 10
  const gradePct = gradeMax > 0 ? (classroom.avg_grade / gradeMax) * 100 : 0
  const gradeDisplay = `${formatGrade(classroom.avg_grade, gradeScale)} / ${gradeMax}`

  const deliveryDisplay = formatPercent(classroom.delivery_rate)
  // A classroom with no graded work and a classroom failing look nothing
  // alike; before PRD-59 they rendered identically.
  const hasGrades = classroom.avg_grade > 0
  const hasRecords = classroom.total_students > 0 && classroom.delivery_rate > 0
  const dash = t("card.stats.dash")

  return (
    <>
      {/* The whole card is clickable, but it is no longer an `<a>` wrapped
          around everything — that put three `<button>`s inside an anchor,
          which the HTML spec forbids and a screen reader reads as one link
          whose name is every label on the card run together ("Matemáticas 3°A
          Editar salón … Ver materiales Ver alumnos"). The link is now a
          stretched overlay with its own name, and the real controls sit above
          it (`relative z-10`). Same click target, valid document, one name per
          control (PRD-59). */}
      <article
        className={cn(
          "rounded-card group relative h-full",
          // PRD-45 §FR-4.3 — muted, not hidden: the point of this tab
          // is "can I still find it", not "is it easy to work in".
          classroom.is_archived && "opacity-75 saturate-50",
        )}
      >
        <Card className="h-full transition-shadow hover:shadow-md">
          <Link
            to={`/salones/${classroom.id}/alumnos`}
            aria-label={t("card.open_roster_aria", { name: classroom.effective_display_name })}
            className="rounded-card absolute inset-0"
          />
          <CardBody className="flex h-full flex-col gap-4">
            {/* Identity */}
            <header>
              {/* `flex-wrap` + a 12rem basis on the title column, instead of
                  a header locked to one row. The chip cluster (student count
                  + at-risk badge) is ~150px, and these cards are ~310-330px
                  wide in BOTH grids, so on one row the title was left with
                  96px — "Matemátic…", which renders two different classrooms
                  identically. Now the chips drop to their own line only when
                  the title would otherwise be squeezed under 12rem; a card
                  with a short name and no badge keeps the original single
                  row. No media query: what matters is the card's width, and
                  the same viewport gives this card two different widths in
                  the /salones grid vs the home rail. */}
              <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1.5">
                <div className="min-w-0 grow basis-48">
                  <div className="flex items-center gap-1.5">
                    {/* Read-only subject-tinted icon (PRD-30). Editing name +
                        icon lives in the edit modal, opened via the pencil. */}
                    <ClassroomIcon classroom={classroom} className="h-5 w-5 shrink-0" />
                    <h2 className="text-ink truncate font-semibold">
                      {classroom.effective_display_name}
                    </h2>
                    {/* PRD-59 — always visible, and 44px on a phone. This was
                        `h-5 w-5 opacity-0 group-hover:opacity-100`: a 20px
                        target that never appears on a device with no hover,
                        which is where this product is used. Archiving lives
                        behind it (PRD-45), so on a phone that flow had no
                        entrance at all. */}
                    <button
                      type="button"
                      onClick={openEdit}
                      aria-label={t("card.edit_aria")}
                      className="rounded-control text-ink-subtle hover:bg-inset hover:text-ink relative z-10 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center transition-colors md:h-8 md:w-8"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                  {/* PRD-38 FR-20 — `semester` holds the school YEAR, so it
                      reads "Ciclo escolar 2025-2026"; the PROGRAM-WIDE period
                      (when set) follows, labelled by the institution's
                      period_type: "· Semestre 3".

                      FR-30 now also puts the period in the title
                      ("Matemáticas 3°A"), so the number appears twice. Kept
                      deliberately: the title's "3°" is a bare ordinal whose
                      UNIT is invisible — a trimestral and a semestral
                      institution both render "3°" — and this line is the only
                      surface that says which. It is also the only one that
                      survives a custom alias, which can drop the period
                      entirely. One repeated digit is a cheaper cost than an
                      unlabelled one. */}
                  <p className="text-meta mt-0.5">
                    {classroom.subject_name} · {t("card.cycle", { cycle: classroom.semester })}
                    {classroom.period_number !== null && (
                      <>
                        {" · "}
                        {t(`card.period.${periodType}`, { number: classroom.period_number })}
                      </>
                    )}
                  </p>
                  {hasCustomAlias && (
                    <p className="text-meta mt-0.5">
                      {t("card.canonical_subtitle", { label: classroom.canonical_label })}
                    </p>
                  )}
                  {/* PRD-61 §FR-5.3 — the cut in course and when it closes,
                      from the school's calendar. One quiet line: the date is
                      the news, the roster's Resultados section is the detail.
                      The calendar mark makes it read as a date, not as a
                      heading for the "Promedio" below it — that figure is the
                      whole course's, not the cut's. */}
                  {classroom.current_cut && !classroom.is_archived && (
                    <p className="text-meta mt-0.5 flex items-center gap-1">
                      <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {t("card.current_cut", {
                        cut: cutFormat.label(classroom.current_cut),
                        closes: cutFormat.closes(classroom.current_cut.end_date),
                      })}
                    </p>
                  )}
                </div>
                {/* Top-right chip cluster — student count + (optional)
                    at-risk badge. Both are `flex-shrink-0` so a long
                    classroom title truncates with ellipsis on the left
                    column instead of compressing the chips. Student count
                    renders icon-only ("👥 28") because the visible word
                    "alumnos" is redundant next to the icon — aria-label
                    carries the full phrase for screen readers. */}
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {classroom.is_archived && (
                    <Badge variant="neutral">{t("card.archived_badge")}</Badge>
                  )}
                  <Badge
                    className="tabular-nums"
                    aria-label={t("card.stats.students_count", {
                      count: classroom.total_students,
                    })}
                  >
                    <Users className="h-3 w-3" aria-hidden />
                    {classroom.total_students}
                  </Badge>
                  {/* At-risk stops mattering once a classroom is archived
                      — it's not being actively taught, so the signal
                      would just be stale noise next to "Archivado". */}
                  {classroom.at_risk_count > 0 && !classroom.is_archived && (
                    <Badge variant="attention">
                      {t("card.at_risk", { count: classroom.at_risk_count })}
                    </Badge>
                  )}
                </div>
              </div>
              {adaptivePending > 0 ? (
                // A control above the stretched link, like "Ver materiales":
                // the card keeps exactly one link.
                <button
                  type="button"
                  onClick={handleReviewAdaptive}
                  aria-label={t("card.adaptive_pending_aria", {
                    count: adaptivePending,
                    name: classroom.effective_display_name,
                  })}
                  className="bg-attention-soft text-attention-ink hover:border-attention focus-visible:ring-accent relative z-10 mt-2 inline-flex min-h-11 w-fit items-center gap-1 rounded-full border border-transparent px-2.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 md:min-h-7"
                >
                  <ChartNoAxesColumnIncreasing className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {t("card.adaptive_pending", { count: adaptivePending })}
                </button>
              ) : null}
              {classroom.is_archived && classroom.archived_at && (
                <p className="text-meta mt-0.5">
                  {t("card.archived_since", { date: formatDate(classroom.archived_at, "PP") })}
                </p>
              )}
            </header>

            {/* Metrics — Promedio + Entregas as labeled, colored bars */}
            <section className="space-y-3">
              <MetricRow
                label={t("card.stats.avg_grade")}
                display={hasGrades ? gradeDisplay : dash}
                pct={gradePct}
                empty={!hasGrades}
                ariaLabel={t("card.stats.avg_grade_aria", {
                  value: formatGrade(classroom.avg_grade, gradeScale),
                  max: gradeMax,
                })}
              />
              <MetricRow
                label={t("card.stats.delivery_rate")}
                display={hasRecords ? deliveryDisplay : dash}
                pct={classroom.delivery_rate}
                empty={!hasRecords}
                ariaLabel={t("card.stats.delivery_rate_aria", { value: deliveryDisplay })}
              />
            </section>

            {/* Footer — mt-auto pins the row to the card bottom so heights
                stay aligned across the grid. PRD-45 §FR-4.3: an archived
                card swaps the two active-state CTAs (Ver materiales / Ver
                alumnos) for a single "Restaurar" — the point of the
                Archivados tab is triage (bring it back or leave it), not
                day-to-day use, so there's nothing to weight toward. */}
            <footer className="mt-auto flex items-center justify-between gap-2 pt-2">
              {classroom.is_archived ? (
                <button
                  type="button"
                  onClick={handleRestore}
                  disabled={restore.isPending}
                  className="rounded-control border-border text-ink-muted hover:bg-accent-soft hover:text-accent-ink relative z-10 inline-flex min-h-11 w-full items-center justify-center gap-1.5 border px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 md:min-h-8"
                >
                  {restore.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <ArchiveRestore className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {t("card.actions.restore")}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleViewMaterials}
                    className="rounded-control border-border text-ink-muted hover:bg-accent-soft hover:text-accent-ink relative z-10 inline-flex min-h-11 items-center gap-1 border px-2 py-1 text-xs font-medium transition-colors md:min-h-8"
                  >
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                    {t("card.actions.view_materials")}
                  </button>
                  {/* Decoration for the stretched link, not a control: it is
                      `aria-hidden` because the link already carries that name,
                      and `pointer-events-none` so the click lands on the
                      overlay underneath it. */}
                  <span
                    aria-hidden
                    className="rounded-control bg-accent text-ink-inverted group-hover:bg-accent-hover pointer-events-none inline-flex min-h-11 items-center gap-1 px-2.5 py-1 text-xs font-semibold transition-colors md:min-h-8"
                  >
                    {t("card.actions.view_roster")}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </span>
                </>
              )}
            </footer>
          </CardBody>
        </Card>
      </article>

      {/* Rendered OUTSIDE the <Link> so the modal's controls are not DOM
          descendants of the anchor (no native-navigation footgun). */}
      <EditClassroomModal open={editing} onClose={() => setEditing(false)} classroom={classroom} />
    </>
  )
}
